import React, { useEffect, useState, useCallback } from 'react';
import styled from 'App/styled';
import { useTheme } from 'emotion-theming';
import { FormattedMessage } from '@nykaa/intl-shared';
import { webViewCallbackhandler } from '@nykaa/webview-wizard';
import { webViewCallbacks } from '@nykaa/webview-wizard/constant';

import Portal from 'shared/components/Portal';
import { useGamoogaData } from 'shared/components/Header/RightNav/Notification';
import { getViewBagPageSource, handleCartNotificationOnclick } from 'shared/components/Header/RightNav/Notification/helpers';
import { VIEW_BAG } from 'constants/strings';
import { getCartViewLocation } from './helper';
import { isDeviceNotWeb } from 'helpers/utils/device';
import { Device } from '@nykaa/data-layer/constants';

const FALLBACK_ID = 'snackbar';
const FALLBACK_ACTIVE_CLASS = 'active';
const DISPLAY_TIME = 2500;
const WAIT_TIME_FOR_CLOSE_ANIMATION = 300; // This is equal to CSS transition animation duration;

interface SnackbarTheme {
  background: string;
}

const Wrapper = styled.div`
  position: fixed;
  z-index: 10;
  bottom: 0;
  left: 0;
  right: 0;
  background: transparent;
  .container {
    height: 100%;
    background: ${({ background }: SnackbarTheme) => background};
    color: #fff;
    ${({ theme }) => theme.typography.bodyLarge};
    min-height: 55px;
    padding: 0 10px;
    margin-bottom: -80px;
    border-radius: 10px;
    transition: all 0.3s;

    &.${FALLBACK_ACTIVE_CLASS} {
      margin: 5px;
    }

    .body {
      height: 52px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      p {
        margin-left: 10px;
      }
    }

    .close-snackbar {
      background: transparent;
      padding: 4px 10px;
      ${({ theme }) => theme.typography.type200};
      border: none;
      color: #fff;

      &:focus {
        outline: none;
      }
    }
  }
`;
const ViewBagButton = styled.div`
  margin-right: 10px;
  color: ${({ theme }) => theme.colors.primaryInverse};
  ${({ theme }) => theme.typography.buttonMedium};
`;
interface SnackbarProps {
  content: string | React.ReactNode;
  autoClose: boolean;
  closeCallback: Function;
  errorType?: boolean;
  snackBarId: string;
  pageType?: string;
  viewBagShow?: boolean;
  device: Device;
}

function AddToCartSnackBar({
  content,
  autoClose,
  closeCallback,
  errorType = false,
  snackBarId = FALLBACK_ID,
  pageType = '',
  viewBagShow = true,
  device,
}: SnackbarProps) {
  const [isMounted, setIsMounted] = useState(false);
  const theme: Theme = useTheme();
  const gamoogaCartNotification = useGamoogaData();
  const handleMount = useCallback(() => {
    if (isMounted) {
      const snackBarNode = document.getElementById(snackBarId);
      if (snackBarNode) {
        snackBarNode.classList.add(FALLBACK_ACTIVE_CLASS);
      }
    }
  }, [isMounted, snackBarId]);

  const handleClose = useCallback(() => {
    if (isMounted) {
      const snackBarNode = document.getElementById(snackBarId);
      if (snackBarNode) {
        snackBarNode.classList.remove(FALLBACK_ACTIVE_CLASS);
        setTimeout(() => {
          closeCallback();
        }, WAIT_TIME_FOR_CLOSE_ANIMATION);
      }
    }
  }, [isMounted, snackBarId, closeCallback]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    handleMount();
  }, [handleMount]);
  useEffect(() => {
    if (autoClose) {
      setTimeout(() => {
        handleClose();
      }, DISPLAY_TIME);
    }
  }, [autoClose, handleClose]);
  const handleClick = () => {
    const cartViewLocation = getCartViewLocation(pageType);
    const pageSource = getViewBagPageSource();
    if (isDeviceNotWeb(device)) {
      const appParams = {
        device,
        callbackFunctionName: webViewCallbacks.showCart,
        callbackParams: '',
      };
      webViewCallbackhandler(appParams);
    } else {
      handleCartNotificationOnclick({
        gamoogaCartNotification,
        cartViewLocation,
        pageSource,
      });
    }
  };
  const background = errorType
    ? theme.brickColor
    : 'linear-gradient(0deg, rgba(101, 119, 134, 0.16), rgba(101, 119, 134, 0.16)), linear-gradient(0deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03)), #0E0F10;';

  return (
    <Portal>
      <Wrapper background={background}>
        <div
          className="container"
          id={snackBarId}
        >
          <div className="body">
            <p>{content}</p>
            {viewBagShow && (
              <ViewBagButton
                id="view_bag_snackbar"
                onClick={handleClick}
              >
                <FormattedMessage id={VIEW_BAG} />
              </ViewBagButton>
            )}
          </div>
          {!autoClose && (
            <button
              type="button"
              className="close-snackbar"
              onClick={handleClose}
            >
              &#10006;
            </button>
          )}
        </div>
      </Wrapper>
    </Portal>
  );
}

export default AddToCartSnackBar;
